import * as React from 'react';
import { ChevronDown } from 'lucide-react';

export interface TopNavItemProps {
  /**
   * Text label for the nav item
   */
  children: React.ReactNode;
  /**
   * Whether this nav item is currently active/selected
   */
  active?: boolean;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
}

/**
 * TopNavItem component - Arbor Design System
 *
 * A navigation item for the top navigation bar with text and chevron icon.
 * Supports default, hover, focused, and active states.
 */
export const TopNavItem = React.forwardRef<HTMLButtonElement, TopNavItemProps>(
  (
    {
      children,
      active = false,
      onClick,
      className,
      style,
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    // Determine colors based on state
    const textColor = active ? '#005700' : '#474747';
    const iconColor = active ? '#005700' : '#474747';

    // Determine background based on state
    const getBackgroundColor = () => {
      if (active) return '#f0faf3';
      if (isHovered) return '#f8f8f8';
      return 'transparent';
    };

    const containerStyles: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      padding: '4px 8px',
      backgroundColor: getBackgroundColor(),
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      boxSizing: 'border-box',
      transition: 'background-color 0.15s ease-in-out',
      outline: 'none',
      boxShadow: isFocused ? '0px 0px 0px 3px #3cad51' : 'none',
      ...style,
    };

    const textStyles: React.CSSProperties = {
      fontSize: '13px',
      fontWeight: 600,
      color: textColor,
      lineHeight: 1.5,
      textAlign: 'center',
      whiteSpace: 'nowrap',
    };

    return (
      <button
        ref={ref}
        className={className}
        style={containerStyles}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        type="button"
      >
        <span style={textStyles}>{children}</span>
        <ChevronDown size={12} color={iconColor} strokeWidth={2} />
      </button>
    );
  }
);

TopNavItem.displayName = 'TopNavItem';
